import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Volume2, Eye, EyeOff, SkipForward, RotateCcw, CheckCircle, Play, Pause } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { selectMaleVoice, selectFemaleVoice, preloadVoices, speakHybrid } from '@/services/voiceService';
import { personalityAPI } from '@/services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Premium Lira avatar images (consistent across platform)
const LIRA_AVATARS = {
  female: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&crop=face',
  male: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face'
};

// Listening exercises by level
const LISTENING_EXERCISES = {
  beginner: [
    { id: 1, text: "Hello! My name is Lira.", translation: "Ola! Meu nome e Lira." },
    { id: 2, text: "How are you today?", translation: "Como voce esta hoje?" },
    { id: 3, text: "Nice to meet you!", translation: "Prazer em conhecer voce!" },
    { id: 4, text: "I am from Brazil.", translation: "Eu sou do Brasil." },
    { id: 5, text: "What is your name?", translation: "Qual e o seu nome?" },
    { id: 6, text: "I like coffee.", translation: "Eu gosto de cafe." },
    { id: 7, text: "Where do you live?", translation: "Onde voce mora?" },
    { id: 8, text: "I work in an office.", translation: "Eu trabalho em um escritorio." },
    { id: 9, text: "Do you speak English?", translation: "Voce fala ingles?" },
    { id: 10, text: "Yes, a little bit.", translation: "Sim, um pouquinho." },
  ],
  intermediate: [
    { id: 1, text: "I've been learning English for two years.", translation: "Eu estou aprendendo ingles ha dois anos." },
    { id: 2, text: "Could you repeat that, please?", translation: "Voce poderia repetir isso, por favor?" },
    { id: 3, text: "I would like to make a reservation.", translation: "Eu gostaria de fazer uma reserva." },
    { id: 4, text: "What time does the movie start?", translation: "A que horas o filme comeca?" },
    { id: 5, text: "I need to catch the next train.", translation: "Eu preciso pegar o proximo trem." },
    { id: 6, text: "How much does this cost?", translation: "Quanto isso custa?" },
    { id: 7, text: "I'm looking for a birthday gift.", translation: "Estou procurando um presente de aniversario." },
    { id: 8, text: "The weather is really nice today.", translation: "O tempo esta muito bom hoje." },
    { id: 9, text: "I'll have the chicken, please.", translation: "Vou querer o frango, por favor." },
    { id: 10, text: "Can I pay by credit card?", translation: "Posso pagar com cartao de credito?" },
  ],
  advanced: [
    { id: 1, text: "I was wondering if you could help me with something.", translation: "Eu estava pensando se voce poderia me ajudar com algo." },
    { id: 2, text: "It's been a while since we last spoke.", translation: "Ja faz um tempo desde que conversamos pela ultima vez." },
    { id: 3, text: "I couldn't agree more with your perspective.", translation: "Nao poderia concordar mais com sua perspectiva." },
    { id: 4, text: "As far as I'm concerned, this is the best option.", translation: "Na minha opiniao, esta e a melhor opcao." },
    { id: 5, text: "I've always wanted to travel around the world.", translation: "Eu sempre quis viajar pelo mundo." },
  ]
};

const ListeningPracticePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const synthRef = useRef(window.speechSynthesis);
  const currentAudioRef = useRef(null); // For premium TTS
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showText, setShowText] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [speed, setSpeed] = useState(0.85);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [completedExercises, setCompletedExercises] = useState([]);
  const [voiceGender, setVoiceGender] = useState('female');
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  
  const level = (user?.level || 'beginner').toLowerCase();
  const exercises = LISTENING_EXERCISES[level] || LISTENING_EXERCISES.beginner;
  const currentExercise = exercises[currentIndex];

  // Load voice gender from user settings and listen for changes
  useEffect(() => {
    const fetchVoiceGender = async () => {
      try {
        // First check localStorage for immediate response
        const savedGender = localStorage.getItem('lira_voice_gender');
        if (savedGender) {
          setVoiceGender(savedGender);
        }
        
        // Then fetch from API
        const personality = await personalityAPI.get();
        if (personality?.voice_gender) {
          setVoiceGender(personality.voice_gender);
        }
      } catch (error) {
        console.error('Failed to fetch voice gender:', error);
      }
    };
    
    fetchVoiceGender();
    
    // Listen for voice changes from Settings
    const handleVoiceChange = (event) => {
      const { gender } = event.detail;
      setVoiceGender(gender);
    };
    
    window.addEventListener('liraVoiceChanged', handleVoiceChange);
    
    return () => {
      window.removeEventListener('liraVoiceChanged', handleVoiceChange);
    };
  }, []);

  useEffect(() => {
    preloadVoices().then(() => setVoicesLoaded(true));
  }, []);

  const getLanguageCode = (language) => {
    const codes = {
      'English': 'en-US',
      'Spanish': 'es-ES',
      'Portuguese': 'pt-BR',
      'French': 'fr-FR',
      'German': 'de-DE',
      'Chinese': 'zh-CN'
    };
    return codes[language] || 'en-US';
  };

  const speakExercise = async (customSpeed = null) => {
    if (isSpeaking) {
      synthRef.current.cancel();
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      setIsSpeaking(false);
      return;
    }

    const targetLang = user?.target_language || user?.learning_language || 'English';
    const finalSpeed = customSpeed !== null ? customSpeed : speed;
    
    try {
      const result = await speakHybrid(currentExercise.text, {
        language: targetLang,
        gender: voiceGender,
        speed: finalSpeed,
        onStart: () => setIsSpeaking(true),
        onEnd: () => setIsSpeaking(false),
        onError: () => setIsSpeaking(false)
      });
      
      // Store audio reference for premium TTS
      if (result instanceof Audio) {
        currentAudioRef.current = result;
      }
    } catch (error) {
      console.error('speakExercise error:', error);
      setIsSpeaking(false);
    }
  };

  const nextExercise = () => {
    synthRef.current.cancel();
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    setIsSpeaking(false);
    setShowText(false);
    setShowTranslation(false);
    if (currentIndex < exercises.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
      toast.success('🎉 Você completou todas as frases! Recomeçando...');
    }
  };

  const prevExercise = () => {
    synthRef.current.cancel();
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    setIsSpeaking(false);
    setShowText(false);
    setShowTranslation(false);
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const markAsUnderstood = async () => {
    if (!completedExercises.includes(currentExercise.id)) {
      setCompletedExercises([...completedExercises, currentExercise.id]);
      
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/gamification/add-xp`, { xp: 5, action: 'listening_exercise' }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('+5 XP! Boa prática! 🎧');
      } catch (error) {
        console.error('Failed to add XP');
      }
    }
    nextExercise();
  };

  const progress = ((currentIndex + 1) / exercises.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900" data-testid="listening-page">
      {/* Header */}
      <header className="p-4">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-white/80 hover:text-white hover:bg-white/10"
            data-testid="listening-back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Voltar
          </Button>
          
          <div className="text-center">
            <h1 className="text-white font-semibold">Prática de Escuta</h1>
            <p className="text-white/60 text-xs">{currentIndex + 1} de {exercises.length}</p>
          </div>
          
          <div className="w-20" /> {/* Spacer */}
        </div>
        
        {/* Progress Bar */}
        <div className="max-w-2xl mx-auto mt-4">
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-gradient-to-r from-purple-400 to-pink-400"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-8 max-w-2xl mx-auto">
        
        {/* Tutor Card - Premium Design */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative mb-8"
        >
          {/* Voice indicator - configured in Settings */}
          <div className="absolute top-0 right-0 z-10">
            <div className="flex items-center gap-2 bg-black/30 backdrop-blur-sm rounded-full px-3 py-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                voiceGender === 'female' ? 'bg-pink-500' : 'bg-blue-500'
              }`}>
                <span className="text-white text-xs">{voiceGender === 'female' ? '♀' : '♂'}</span>
              </div>
              <span className="text-white/80 text-xs">Lira</span>
            </div>
          </div>

          {/* Tutor Image */}
          <div className="flex flex-col items-center">
            <div className={`relative ${isSpeaking ? 'animate-pulse' : ''}`}>
              <div className={`w-28 h-28 rounded-full overflow-hidden border-4 ${
                isSpeaking ? 'border-purple-400 shadow-lg shadow-purple-500/50' : 'border-white/20'
              } transition-all duration-300`}>
                <img 
                  src={LIRA_AVATARS[voiceGender]}
                  alt="Lira"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Speaking Indicator */}
              {isSpeaking && (
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                  <div className="flex gap-0.5 bg-purple-500 px-3 py-1.5 rounded-full">
                    <span className="w-1 h-3 bg-white rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                    <span className="w-1 h-4 bg-white rounded-full animate-bounce" style={{animationDelay: '100ms'}} />
                    <span className="w-1 h-3 bg-white rounded-full animate-bounce" style={{animationDelay: '200ms'}} />
                    <span className="w-1 h-4 bg-white rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                    <span className="w-1 h-3 bg-white rounded-full animate-bounce" style={{animationDelay: '400ms'}} />
                  </div>
                </div>
              )}
            </div>
            
            <p className="text-white/60 text-sm mt-4">
              Lira • {voiceGender === 'female' ? 'Voz Feminina' : 'Voz Masculina'}
            </p>
          </div>
        </motion.div>

        {/* Content Card - Glassmorphism */}
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 mb-6 border border-white/10"
        >
          {/* Text Display */}
          <div className="min-h-[100px] flex flex-col items-center justify-center mb-6">
            <AnimatePresence mode="wait">
              {showText ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="text-center"
                >
                  <p className="text-2xl font-medium text-white mb-3">
                    &ldquo;{currentExercise.text}&rdquo;
                  </p>
                  {showTranslation && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-white/60 text-base"
                    >
                      {currentExercise.translation}
                    </motion.p>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Volume2 className="w-8 h-8 text-white/60" />
                  </div>
                  <p className="text-white/60 text-sm">
                    Ouça atentamente e tente entender
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Speed Controls */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className="text-white/40 text-xs mr-2">Velocidade</span>
            {[
              { value: 0.6, label: '0.6x' },
              { value: 0.8, label: '0.8x' },
              { value: 1.0, label: '1x' },
              { value: 1.2, label: '1.2x' },
            ].map((s) => (
              <button
                key={s.value}
                onClick={() => setSpeed(s.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  speed === s.value
                    ? 'bg-purple-500 text-white'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Main Play Button */}
          <button
            onClick={() => speakExercise()}
            className={`w-full py-5 rounded-2xl font-medium text-lg flex items-center justify-center gap-3 transition-all ${
              isSpeaking
                ? 'bg-purple-500 text-white'
                : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90'
            }`}
            data-testid="play-btn"
          >
            {isSpeaking ? (
              <>
                <Pause className="w-6 h-6" />
                Pausar
              </>
            ) : (
              <>
                <Play className="w-6 h-6" />
                Ouvir Frase
              </>
            )}
          </button>
        </motion.div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          <Button
            onClick={() => setShowText(!showText)}
            variant="outline"
            className="flex-1 py-4 rounded-xl bg-white/5 border-white/10 text-white hover:bg-white/10"
          >
            {showText ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {showText ? 'Esconder' : 'Ver texto'}
          </Button>
          
          <Button
            onClick={() => setShowTranslation(!showTranslation)}
            variant="outline"
            className="flex-1 py-4 rounded-xl bg-white/5 border-white/10 text-white hover:bg-white/10"
            disabled={!showText}
          >
            🌐 {showTranslation ? 'Esconder' : 'Tradução'}
          </Button>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            onClick={prevExercise}
            variant="ghost"
            className="text-white/60 hover:text-white hover:bg-white/10"
            disabled={currentIndex === 0}
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Anterior
          </Button>

          <Button
            onClick={markAsUnderstood}
            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-5 rounded-2xl font-medium"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Entendi!
          </Button>

          <Button
            onClick={nextExercise}
            variant="ghost"
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            Próxima
            <SkipForward className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Tip */}
        <p className="text-center text-white/40 text-xs mt-8">
          💡 Ouça várias vezes antes de ver o texto
        </p>
      </main>
    </div>
  );
};

export default ListeningPracticePage;
